import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const tryCatchPatternCode = `// Engineering Pattern: Granular Error Handling
try {
    const rawData = fetchDataFromAPI(); 
    const config = JSON.parse(rawData);
    initializeSystem(config);
} catch (error) {
    if (error instanceof SyntaxError) {
        console.error("Invalid Configuration Format:", error.message);
    } else if (error.name === "NetworkError") {
        console.warn("Retrying connection...");
    } else {
        // Rethrow original error if it's unhandled to avoid silent failures
        throw error; 
    }
} finally {
    // Critical: Runs regardless of success or failure
    setLoadingState(false);
}`;

const finallyCleanupCode = `// Technical Insight: Resource Management
async function processLargeFile(filePath) {
    const handle = await openFile(filePath);
    try {
        await parseData(handle);
    } catch (err) {
        logErrorToSentry(err);
    } finally {
        // Essential: Prevents memory leaks/locked files
        // This executes even if an error is thrown or 'return' is called
        await closeFile(handle); 
    }
}`;

const JSTryCatch = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Runtime Resilience</div>
                <h1>Try/Catch & Error Types</h1>
                <p className="hero-lead">
                    Master the mechanics of exception handling. Learn how to intercept
                    runtime failures, prevent application crashes, and manage
                    resource cleanup using the `try...catch...finally` protocol.
                </p>
            </div>

            <div className="content-section">
                <h2>The Anatomy of an Exception</h2>
                <p>
                    When JavaScript encounters a runtime error, it "throws" an **Error Object**.
                    This object is more than just a message; it contains a `name`
                    (e.g., `TypeError`), a `message`, and crucially, a `stack` trace.
                    Without a `try...catch` block, this error "bubbles up" the call
                    stack. If it reaches the top without being handled, the browser
                    terminates the script execution.
                </p>
                <CodeBlock code={tryCatchPatternCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Resource Cleanup with Finally</h2>
                <p>
                    One of the most overlooked aspects of error handling is **Resource Management**.
                    When your code opens a file handle, database connection, or network
                    socket, a failure in the processing logic can leave that resource
                    hanging. The `finally` block guarantees execution regardless of
                    whether an error occurred or a `return` statement was hit,
                    ensuring system stability.
                </p>
                <CodeBlock code={finallyCleanupCode} language="javascript" />
            </div>

            <ProTip><p>
                    JavaScript exceptions are not localized to the line they occur on.
                    They search for the nearest `catch` block in the current call stack.
                    If `Function A` calls `Function B`, and `B` throws an error, `A`
                    can catch it. This allows you to centralize error handling at
                    the controller or high-level application layer rather than
                    littering every function with duplicate `try...catch` logic.
                </p></ProTip>

            <KeyFacts><ul>
                    <li>**Specificity:** Catch specific error types using `instanceof` to provide tailored recovery.</li>
                    <li>**No Silent Failures:** Never leave an empty `catch {"{ }"}` block; at minimum, log the error.</li>
                    <li>**Cleanup:** Always use `finally` to close connections or reset UI loading states.</li>
                    <li>**Rethrow:** If you can't resolve the error at the current level, `throw` it again.</li>
                    <li>**Logging:** Capture the `error.stack` in your logs to identify the root cause in production.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSTryCatch;




